import * as angular from "angular";
import {Link} from "../models/link.model";
import {Group} from "../models/groups/group.model";
import {Instructor} from "../models/instructor.model";
import {StudyProgram} from "../models/study-program.model";
import {MessagingService, MessageObject} from "../common/messaging/messaging.service";
import {Institution} from "../models/institution.model";
import {GroupsInit} from "./groups-init/groups-init.service";
import {GroupsService} from "../common/groups/groups.service";
import {UserSessionService} from "../common/user-session/user-session.service";
import {TestprepRootScope} from "../models/testprep-rootscope.model";

const INITDATA = "initData";
const GROUPSPERPAGE = 10;
const MESSAGE_TIMEOUT = 8000;
const DEFAULT_GRADES_QUERY = "?defaultGrades=9,10,11,12";

export class GroupsController {
    crumbs: Link[];
    district: boolean;
    districtAdmin: boolean;
    isSchoolAdmin: boolean;
    showSuccessMessage: boolean;
    successMessage: string;
    instructors: Instructor[];
    studyPrograms: StudyProgram[];
    selectedInstructor: Instructor;
    selectedProgram: StudyProgram;
    groups: Group[];
    institutions: Institution[];
    selectedInstitution: Institution;
    filteredGroups: Group[];
    pages: Group[][];
    currentPage: number = 1;
    messages: MessageObject;
    tenantId: string;
    deletePendingConfirmation: number[] = [];
    deleteSuccess: number[] = [];
    deletePendingSuccess : Group;
    tooltipGroup: number;

    /* @ngInject */
    constructor(private $route: angular.route.IRouteService, private groupsService: GroupsService,
                messagingService: MessagingService, private userSessionService: UserSessionService,
                private $rootScope: TestprepRootScope, private $timeout: angular.ITimeoutService) {
        this.messages = messagingService("groups");
    }

    private static sortGroups(groupA: Group, groupB: Group): number {
        let defaultA = groupA.productGroups.TESTPREP.defaultGroup;
        let defaultB = groupB.productGroups.TESTPREP.defaultGroup;
        if (!defaultA && defaultB) { return 1; }
        if (defaultA && !defaultB) { return -1; }
        return 0;
    }

    private static sortInstructors(instructorA: Instructor, instructorB: Instructor): number {
        if (instructorA.name.displayName > instructorB.name.displayName) { return 1; }
        if (instructorA.name.displayName < instructorB.name.displayName) { return -1; }
        return 0;
    }

    private static sortPrograms(programA: StudyProgram, programB: StudyProgram): number {
        if (programA.courseSlug > programB.courseSlug) { return 1; }
        return -1;
    }

    $onInit() {
        let initData: GroupsInit = this.$route.current.locals[INITDATA];
        this.district = initData.district;
        this.districtAdmin = initData.districtAdmin;
        this.isSchoolAdmin = initData.currentUser.isSchoolAdmin();
        this.groups = initData.groups && initData.groups.sort(GroupsController.sortGroups);
        this.tenantId = this.$route.current.params.tenantId;
        if (initData.institution && initData.district) {  this.loadInstitutions(initData.institution); }
        this.updateCrumbs();
        this.checkForSuccessMessage();
        if (!this.groups || !this.groups.length) { return; }
        this.getInstructorList();
        this.getStudyProgramList();
        this.refreshDisplayedGroups();
        this.updateCrumbs();
    }

    getDetailPath(group: Group): string {
        let id = group.productGroups.TESTPREP.groupId;
        if(group.productGroups.TESTPREP.defaultGroup) {
            return `#/${this.tenantId}/default-group-detail/${id}` + DEFAULT_GRADES_QUERY;
        }

        return `#/${this.tenantId}/group-detail/${id}`;
    }

    onSelectSchool(): void {
        this.clearError();
        this.userSessionService.setCurrentSchool(this.selectedInstitution);
        if (this.selectedInstitution) {
            this.fetchGroups();
        } else {
            this.groups = void 0;
        }
        this.updateCrumbs();
    }

    clearError(): void { this.$rootScope.loadingError = false; }

    errorCallback = (): void => {
        this.$rootScope.loadingError = true;
        this.$rootScope.loadingView = false;
    };

    showGroups(): boolean { return !this.district || (this.district && !!this.selectedInstitution); }

    showNoGroupsMessage(): boolean {
        return !this.$rootScope.loadingView && (!this.filteredGroups || !this.filteredGroups.length);
    }

    districtAccessNotAllowed(): boolean { return this.district && !this.districtAdmin; }

    selectForDeletion(group: Group): void {
        let index = this.deletePendingConfirmation.indexOf(group.groupId);
        if(index < 0) {
            this.tooltipGroup = 0;
            this.deletePendingConfirmation.push(group.groupId);
        }
    }

    deselectForDeletion(group: Group) {
        let index = this.deletePendingConfirmation.indexOf(group.groupId);
        if(index >= 0) {
            this.deletePendingConfirmation.splice(index, 1);

        }
    }

    deleteGroup(group: Group): void {
        this.deletePendingSuccess = group;
        this.groupsService.deleteGroup(this.tenantId, group).then(this.handleDeleteGroupResponse);
    }

    filterDeletedGroup(group: Group): void {
        let index = this.groups.indexOf(group);
        let deleteSuccessIndex = this.deleteSuccess.indexOf(group.groupId);

        if(deleteSuccessIndex >= 0 && index >= 0) {
            this.deleteSuccess.splice(deleteSuccessIndex, 1);
            this.groups.splice(index, 1);

            this.refreshDisplayedGroups();
        }
    }

    refreshDisplayedGroups(): void {
        this.refreshFilteredGroups();
        this.pages = [[]];
        this.filteredGroups.forEach((group: Group) => {
            let lastPage = this.pages[this.pages.length - 1];
            if (lastPage.length >= GROUPSPERPAGE) {
                this.pages.push([]);
                lastPage = this.pages[this.pages.length - 1];
            }
            lastPage.push(group);
        });
        this.currentPage = Math.min(this.pages.length, this.currentPage);
    }

    getPageOfGroups(): Group[] { return this.pages && this.pages[this.currentPage - 1]; }

    getGroupCount(): number {
        if(this.filteredGroups && this.filteredGroups.length > 0) {
            return this.filteredGroups.length - this.deleteSuccess.length;
        }

        return 0;
    }

    showPaginator(): boolean { return this.pages && this.pages.length > 1; }

    getMessage(): string {
        if (this.district && !this.districtAdmin) {
            let key = "noDistrictPermission";
            return this.messages[key];
        }
        let key = "noGroups";
        if (this.groups && this.groups.length && (!this.filteredGroups || !this.filteredGroups.length)) {
            key += "ForFilter";
        }
        return this.messages[key];
    }

    isGroupDeleted(group: Group): boolean { return this.deleteSuccess.indexOf(group.groupId) >= 0; }

    isGroupPendingDeletion(group: Group): boolean {
        return this.deletePendingConfirmation.indexOf(group.groupId) >= 0;
    }

    isDeleteShowing(group: Group): boolean {
      return this.isGroupDeleted(group) || !!group.productGroups.TESTPREP.defaultGroup;
    }
    clearSuccessMessage(): void {
        this.showSuccessMessage = false;
        delete this.$rootScope.successMessage;
        delete this.successMessage;
    }

    schoolHasGroups(): boolean {
        return (!!this.groups && !!this.groups.length) || (!this.isSchoolAdmin && !this.district);
    }

    getAssignLink(): string {
        let tenantId = this.tenantId;
        if (this.district) { tenantId = this.selectedInstitution.institutionId; }
        return `#/${tenantId}/assign-programs`;
    }

    setTooltip(group: Group): void {
        this.tooltipGroup = (group && group.groupId) || void 0;
    }

    isTooltipShowing(group: Group): boolean {
        return this.tooltipGroup === group.groupId;
    }

    private checkForSuccessMessage(): void {
        if (this.$rootScope.successMessage) {
            this.successMessage = this.$rootScope.successMessage;
            this.showSuccessMessage = true;
            this.$timeout(() => {
                this.clearSuccessMessage();
                delete this.$rootScope.successMessage;
                delete this.successMessage;
            }, MESSAGE_TIMEOUT);
        }
    }

    private loadInstitutions = (institutions: any) => {
        this.institutions = institutions.children;

        if (this.userSessionService.hasCurrentSchool()) {
            this.selectedInstitution = this.userSessionService.getCurrentSchool();
        } else {
            this.selectedInstitution = null;
        }
        this.onSelectSchool();
    };

    private updateCrumbs(): void {
        if (this.district) {
            this.crumbs = [{ name: "Naviance Test Prep: District" }];
            if (this.selectedInstitution) {
                this.crumbs.push(
                    { name: `${this.selectedInstitution.name}`}
                );
            }
        } else {
             this.crumbs = [{ name: "Naviance Test Prep" }];
        }
    }

    private fetchGroups(): void {
        this.groups = void 0;
        this.instructors = void 0;
        this.studyPrograms = void 0;
        this.filteredGroups = void 0;
        this.pages = [[]];
        this.tenantId = this.selectedInstitution.institutionId;
        this.$rootScope.loadingView = true;
        this.groupsService.getAll(this.tenantId).then(this.loadGroups, this.errorCallback);
    }

    private loadGroups = (groups: Group[]) => {
        this.groups = groups.sort(GroupsController.sortGroups);
        if (this.groups.length) {
            this.getInstructorList();
            this.getStudyProgramList();
            this.refreshDisplayedGroups();
        }
        this.$rootScope.loadingView = false;
    };

    private refreshFilteredGroups(): void {
        this.filteredGroups = this.groups.filter(this.filterGroupsByInstructor)
            .filter(this.filterGroupsByProgram);
    }

    private getInstructorList(): void {
        this.instructors = this.groups.map((group: Group) => group.instructors)
            .reduce((flat: Instructor[], nested: Instructor[]) => flat.concat(nested), [])
            .reduce(this.noInstructorDups,[])
            .sort(GroupsController.sortInstructors);
    }

    private noInstructorDups = (instructors: Instructor[], possible: Instructor): Instructor[] => {
        if (possible && !this.isDuplicate(instructors, possible, "instructorId")) {
            instructors.push(possible);
        }
        return instructors;
    };

    private getStudyProgramList(): void {
        this.studyPrograms = this.groups
            .map((group: Group) => group.productGroups.TESTPREP.studyPrograms)
            .reduce((flat: StudyProgram[], nested: StudyProgram[]) => flat.concat(nested))
            .reduce(this.noProgramDups, [])
            .sort(GroupsController.sortPrograms);
    }

    private noProgramDups = (programs: StudyProgram[], possible: StudyProgram): StudyProgram[] => {
        if (!this.isDuplicate(programs, possible, "courseSlug")) {
            programs.push(possible);
        }
        return programs;
    };

    private isDuplicate(arr: Object[], possibleDup: Object, field: string): boolean {
        return arr.some((item: Object) => item[field] === possibleDup[field]);
    }

    private handleDeleteGroupResponse = (status: number): void => {
        if (this.deletePendingSuccess && status >= 400) {
            this.errorCallback();
        } else {
            let successIndex = this.groups.indexOf(this.deletePendingSuccess);
            if(successIndex < 0) {
                this.errorCallback();
            } else {
                let confirmationIndex = this.deletePendingConfirmation.indexOf(this.deletePendingSuccess.groupId);
                if(confirmationIndex >= 0) {
                    this.deletePendingConfirmation.splice(confirmationIndex, 1);
                }

                this.deleteSuccess.push(this.deletePendingSuccess.groupId);
                this.deleteSuccessMessageTimeout(this.deletePendingSuccess.groupId);
            }
        }

        this.deletePendingSuccess = undefined;
    };

    private deleteSuccessMessageTimeout = (groupId: number): void => {
        let groups = this.groups.filter((group: Group) => { return group.groupId === groupId; });
        this.$timeout(() => { this.filterDeletedGroup(groups[0]); }, MESSAGE_TIMEOUT);
    };

    private filterGroupsByInstructor = (group: Group): boolean => {
        if (!this.selectedInstructor) { return true; }
        return group.instructors && group.instructors.some((instructor: Instructor) => {
            return instructor.instructorId === this.selectedInstructor.instructorId;
        });
    };

    private filterGroupsByProgram = (group: Group): boolean => {
        if (!this.selectedProgram) { return true; }
        return group.productGroups.TESTPREP.studyPrograms.some((program: StudyProgram) => {
            return program.courseSlug === this.selectedProgram.courseSlug;
        });
    };
}



/** WEBPACK FOOTER **
 ** ./~/tslint-loader!./app/groups/groups.controller.ts
 **/